<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import type { FileUploadSelectEvent } from 'primevue/fileupload';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import {
  MAXIMUM_CREW_BRIEFING_TEMPLATE_SIZE,
  useCharterCrewBriefingTemplatesStore,
} from '@/charter/stores/charter-crew-briefing-templates-store.ts';
import { getApiErrorMessages } from '@/util/error-utils.ts';
import { formatFileSize } from '@/util/file-formatting.ts';

type FileUploadRef = {
  clear: () => void;
};

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  'update:visible': [visible: boolean];
  uploaded: [];
}>();

const templatesStore = useCharterCrewBriefingTemplatesStore();
const confirm = useConfirm();
const toast = useToast();
const uploader = useTemplateRef<FileUploadRef>('uploader');
const selectedFile = ref<File | null>(null);

const dialogVisible = computed({
  get: () => props.visible,
  set: (visible: boolean) => emit('update:visible', visible),
});

const validationError = computed(() => {
  const file = selectedFile.value;
  if (!file) {
    return null;
  }
  if (!file.name.toLowerCase().endsWith('.docx')) {
    return 'Select a DOCX file.';
  }
  if (file.size === 0) {
    return 'The selected file is empty.';
  }
  if (file.size > MAXIMUM_CREW_BRIEFING_TEMPLATE_SIZE) {
    return `The selected file exceeds the ${formatFileSize(MAXIMUM_CREW_BRIEFING_TEMPLATE_SIZE)} limit.`;
  }
  return null;
});

const canUpload = computed(
  () => selectedFile.value !== null && validationError.value === null && !templatesStore.uploading,
);

function reset(): void {
  selectedFile.value = null;
  uploader.value?.clear();
}

function close(): void {
  if (!templatesStore.uploading) {
    dialogVisible.value = false;
  }
}

function onSelect(event: FileUploadSelectEvent): void {
  const files = Array.isArray(event.files) ? event.files : [event.files];
  selectedFile.value = files[0] ?? null;
}

function onRemove(): void {
  selectedFile.value = null;
}

function showErrors(error: unknown): void {
  getApiErrorMessages(error, 'Crew briefing template could not be uploaded.').forEach(detail => {
    toast.add({
      severity: 'error',
      summary: 'Upload failed',
      detail,
      life: 5000,
    });
  });
}

async function upload(): Promise<void> {
  const file = selectedFile.value;
  if (!file || validationError.value) {
    return;
  }

  try {
    await templatesStore.uploadTemplate(file);
    toast.add({
      severity: 'success',
      summary: 'Template uploaded',
      detail: `${file.name} was uploaded successfully.`,
      life: 3000,
    });
    emit('uploaded');
    dialogVisible.value = false;
  } catch (error) {
    showErrors(error);
  }
}

function submit(): void {
  const file = selectedFile.value;
  if (!file || validationError.value) {
    return;
  }

  const replacesExistingTemplate = templatesStore.templates.some(
    template => template.fileName?.localeCompare(file.name, undefined, { sensitivity: 'accent' }) === 0,
  );

  if (!replacesExistingTemplate) {
    void upload();
    return;
  }

  confirm.require({
    header: 'Replace template',
    message: `${file.name} already exists. Do you want to replace it?`,
    accept: () => void upload(),
  });
}

watch(() => props.visible, visible => {
  if (!visible) {
    reset();
  }
});
</script>

<template>
  <p-dialog
    v-model:visible="dialogVisible"
    modal
    header="Upload crew briefing template"
    :closable="!templatesStore.uploading"
    :style="{ width: '23.75rem', maxWidth: '95vw' }"
    @hide="reset"
  >
    <p class="mt-0 text-sm text-slate-600">
      Select one DOCX template up to {{ formatFileSize(MAXIMUM_CREW_BRIEFING_TEMPLATE_SIZE) }}.
    </p>

    <p-file-upload
      ref="uploader"
      name="formFile"
      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      :customUpload="true"
      :fileLimit="1"
      :maxFileSize="MAXIMUM_CREW_BRIEFING_TEMPLATE_SIZE"
      :multiple="false"
      :showCancelButton="false"
      :showUploadButton="false"
      :disabled="templatesStore.uploading"
      @select="onSelect"
      @remove="onRemove"
    >
      <template #content>
        <div v-if="selectedFile" class="flex items-center gap-2">
          <span class="material-icons text-slate-500">description</span>
          <span>{{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})</span>
        </div>
      </template>
      <template #empty>
        <div class="py-6 text-center text-slate-500">
          Drag and drop a DOCX file here, or choose a file.
        </div>
      </template>
    </p-file-upload>

    <p-message v-if="validationError" severity="error" :closable="false" class="mt-3">
      {{ validationError }}
    </p-message>

    <template #footer>
      <p-button
        label="Cancel"
        severity="secondary"
        outlined
        :disabled="templatesStore.uploading"
        @click="close"
      />
      <p-button
        label="Upload"
        icon="pi pi-upload"
        :disabled="!canUpload"
        :loading="templatesStore.uploading"
        @click="submit"
      />
    </template>
  </p-dialog>
</template>

<style scoped lang="scss">
.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}
</style>
